// ============================================================================
// Safe Care - Demande de réactivation (côté agent)
// Module : features/assistance_sociale/pages
// Route  : /assurance/demande/suspension-reactive
// Flux   : liste des souscriptions SUSPENDUE -> "Demander la réactivation"
//          -> modale motif -> DEMANDESUSPENSION EN_ATTENTE (TypeDemande =
//          'REACTIVATION'). La souscription reste SUSPENDUE jusqu'à validation
//          sur /assurance/demande/suspension-active.
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import dayjs from 'dayjs';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import SearchModal from '../../../Components/SearchModal';
import ParamsPicklist from '../../../Types/ParamsPicklist';
import {
  useGetAllSouscriptions,
  useDemanderReactivation,
  useGetDemandesSuspension,
  useApercuFrais,
} from '../hooks';
import { SouscriptionSafeCare, DemandeSuspension } from '../types';

const { TextArea } = Input;

// Picklist des motifs de réactivation (table LIB, rubrique MOTREAC — script 26_motifs_reactivation.sql)
const searchMotifReac: ParamsPicklist = { CodeAgence: 'MOTREAC', Table: 'LIB', Colonne1: 'CODLIB', Colonne2: 'LIBELL', Colagence: 'RUBRIQ', Choix: 22 };

const SuspensionReactivePage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const { data: souscriptions, isLoading, refetch } = useGetAllSouscriptions('SUSPENDUE');
  const { data: demandesEnAttente, isLoading: loadingDemandes } = useGetDemandesSuspension('EN_ATTENTE', 'REACTIVATION');
  const demanderReactivation = useDemanderReactivation();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);
  const [modalReactive, setModalReactive] = useState(false);
  const [motif, setMotif] = useState('');
  const [formMotif] = Form.useForm();

  // Frais de réactivation : grille du code opération global appliquée à la
  // cotisation de la version de la souscription ; débités à la validation.
  const { data: apercuFraisRes, isLoading: loadingFrais } = useApercuFrais('REACTIVATION', { idSouscription: modalReactive ? selectedSouscription?.IdSouscription : undefined });
  const apercuFrais = apercuFraisRes?.Result?.Table1?.[0];
  const fraisCalcules = apercuFraisRes?.StatutCode === 200;
  const fraisReacMontant = Number(apercuFrais?.Frais) || 0;

  // Souscriptions SUSPENDUE sans demande de réactivation déjà en attente
  const idsEnAttente = useMemo(
    () => new Set((demandesEnAttente ?? []).map((d: DemandeSuspension) => d.IdSouscription)),
    [demandesEnAttente]
  );

  const filteredData = useMemo(() => {
    const list = (souscriptions ?? []).filter((s) => !idsEnAttente.has(s.IdSouscription!));
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase();
    return list.filter((d) =>
      [d.Numcli, d.NOMCLI, d.PRECLI, d.NomProduit, d.NumeroVersion, d.NumCptDebit, d.MotifStatut]
        .map((v) => String(v ?? '').toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [souscriptions, searchTerm, idsEnAttente]);

  const columns: ColumnsType<SouscriptionSafeCare> = [
    { title: 'N°', dataIndex: 'IdSouscription', width: 70 },
    { title: 'Client', dataIndex: 'Numcli', width: 100 },
    { title: 'Nom', dataIndex: 'NOMCLI', ellipsis: true },
    { title: 'Prénom', dataIndex: 'PRECLI', ellipsis: true },
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 75 },
    { title: 'Compte', dataIndex: 'NumCptDebit', width: 130 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 110, align: 'right', render: (v: number) => (v ?? 0).toLocaleString('fr-FR') },
    { title: 'Motif suspension', dataIndex: 'MotifStatut', ellipsis: true },
  ];

  const columnsDemandes: ColumnsType<DemandeSuspension> = [
    { title: 'N°', dataIndex: 'IdDemandeSuspension', width: 70 },
    { title: 'Souscription', dataIndex: 'IdSouscription', width: 110 },
    { title: 'Client', dataIndex: 'Numcli', width: 100 },
    { title: 'Nom', dataIndex: 'NOMCLI', ellipsis: true },
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true },
    { title: 'Motif', dataIndex: 'Motif', ellipsis: true },
    { title: 'Date demande', dataIndex: 'DatCre', width: 110, render: (v: string) => (v ? dayjs(v).format('DD/MM/YYYY') : '') },
    { title: 'Saisi par', dataIndex: 'UtiSai', width: 110 },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: SouscriptionSafeCare[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedSouscription(selectedRows?.[0] ?? null);
  };

  const openModal = () => {
    if (!selectedSouscription) {
      SweetAlert.fire({ title: 'Sélectionnez une souscription suspendue', icon: 'warning' });
      return;
    }
    setMotif('');
    formMotif.resetFields();
    setModalReactive(true);
  };

  const confirmReactive = () => {
    if (!selectedSouscription) return;
    if (!motif.trim()) {
      SweetAlert.fire({ title: 'Motif obligatoire', icon: 'warning' });
      return;
    }
    setLoading(true);
    demanderReactivation.mutate(
      { idSouscription: selectedSouscription.IdSouscription!, motif, utiSai: user.coduti ?? '' },
      {
        onSuccess: (res) => {
          setLoading(false);
          setModalReactive(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Demande envoyée', text: res.Message, icon: 'success' });
            setSelectedRowKeys([]);
            setSelectedSouscription(null);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: (err: any) => {
          setLoading(false);
          SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
        },
      }
    );
  };

  const actualiser = () => {
    setLoading(true);
    refetch().finally(() => {
      setLoading(false);
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    });
  };

  const annuler = () => {
    setSelectedRowKeys([]);
    setSelectedSouscription(null);
    setSearchTerm('');
  };

  const items = [
    {
      key: '1',
      label: <span id="Tab">Demande de réactivation</span>,
      children: null,
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - DEMANDE DE RÉACTIVATION</b>
      </Typography>
      <Spin spinning={isLoading || loading} size="large" style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign="left" form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block disabled={!selectedSouscription} onClick={openModal}>
                  Demander la réactivation
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block onClick={actualiser}>
                  Actualiser
                </Button>
                <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={annuler}>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs size="small" type="card" defaultActiveKey="1" items={items} animated={{ inkBar: true, tabPane: true }} />
            </div>
          </div>
        </Form>
        <div className="tabItem">
          <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
            <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
              <legend>Souscriptions suspendues ({filteredData.length})</legend>
              <Input.Search
                placeholder="Rechercher une souscription suspendue..."
                allowClear
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ marginBottom: 8, width: 300 }}
              />
              <Table
                size="small"
                style={{ width: '100%' }}
                rowKey="IdSouscription"
                rowSelection={{ type: 'radio', selectedRowKeys, onChange: onSelectChange }}
                onRow={(record) => ({
                  onClick: () => {
                    const key = record.IdSouscription!;
                    if (selectedRowKeys[0] === key) {
                      setSelectedRowKeys([]);
                      setSelectedSouscription(null);
                    } else {
                      onSelectChange([key], [record]);
                    }
                  },
                  style: { cursor: 'pointer' },
                })}
                columns={columns}
                dataSource={filteredData}
                scroll={{ y: 280, x: 10 }}
                pagination={{ defaultPageSize: 8, pageSizeOptions: [8, 15, 30, 50], showSizeChanger: true, position: ['bottomRight'] }}
                locale={{ emptyText: 'Aucune souscription suspendue sans demande en attente' }}
              />
            </Box>

            <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
              <legend>Demandes de réactivation en attente ({demandesEnAttente?.length ?? 0})</legend>
              <Spin spinning={loadingDemandes}>
                <Table
                  size="small"
                  style={{ width: '100%' }}
                  rowKey="IdDemandeSuspension"
                  columns={columnsDemandes}
                  dataSource={demandesEnAttente ?? []}
                  scroll={{ y: 200, x: 10 }}
                  pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20], showSizeChanger: true, position: ['bottomRight'] }}
                  locale={{ emptyText: 'Aucune demande de réactivation en attente' }}
                />
              </Spin>
            </Box>
          </div>
        </div>
      </Spin>

      <Modal
        title="Demande de réactivation"
        open={modalReactive}
        onCancel={() => setModalReactive(false)}
        centered
        zIndex={1400}
        className="safeCareModal"
        footer={[
          <Button key="cancel" onClick={() => setModalReactive(false)}>Annuler</Button>,
          <Button key="ok" type="primary" loading={loading} onClick={confirmReactive}>
            Envoyer la demande
          </Button>,
        ]}
      >
        {selectedSouscription && (
          <Box sx={{ mb: 2 }}>
            <p>Client : <b>{selectedSouscription.NOMCLI} {selectedSouscription.PRECLI} ({selectedSouscription.Numcli})</b></p>
            <p>Produit : <b>{selectedSouscription.NomProduit}</b> (v{selectedSouscription.NumeroVersion})</p>
            <p>Motif de la suspension : <b>{selectedSouscription.MotifStatut ?? '-'}</b></p>
            {/* Frais de réactivation : grille du code opération, débités à la validation */}
            <p>
              Frais de réactivation :{' '}
              <b>
                {loadingFrais ? 'Calcul…' : fraisCalcules
                  ? (fraisReacMontant > 0 ? `${fraisReacMontant.toLocaleString('fr-FR')} FCFA (compte ${selectedSouscription.NumCptDebit})` : 'Aucun')
                  : 'Non calculable'}
              </b>
            </p>
            {!loadingFrais && apercuFraisRes && (
              <p style={{ color: fraisCalcules ? 'rgba(0,0,0,0.6)' : '#d32f2f', fontSize: 12, marginTop: -8 }}>
                {fraisCalcules
                  ? (fraisReacMontant > 0
                    ? `Débités automatiquement à la validation (grille ${apercuFrais?.CodTarif}, palier ${apercuFrais?.NumPalier}, base ${Number(apercuFrais?.MontantBase ?? 0).toLocaleString('fr-FR')} FCFA).`
                    : apercuFraisRes.Message)
                  : `${apercuFraisRes.Message} La validation de la demande sera refusée tant que ce paramétrage n'est pas corrigé.`}
              </p>
            )}
          </Box>
        )}
        {/* Picklist sur la table LIB (rubrique MOTREAC) — le libellé choisi est enregistré comme motif */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
            Motif de la réactivation <span style={{ color: 'red' }}>*</span>
          </label>
          <Form component={false} form={formMotif}>
            <SearchModal
              width={12}
              Titre="Motif de réactivation"
              label=""
              name="MotifReac"
              Taille1={167}
              Taille2={320}
              Parametre={searchMotifReac}
              form={formMotif}
              ExecuteafterSelect1={() => setMotif(String(formMotif.getFieldValue('lib_MotifReac') ?? ''))}
            />
          </Form>
          {!motif && (
            <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
              Sélectionnez un motif dans la liste.
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SuspensionReactivePage;
